import { useMemo, useState } from 'react'
import './PaymentMethodSelector.css'

function PaymentMethodSelector({
  methods,
  selectedMethodId,
  onSelectMethodId,
  theme,
  onNotify,
  formatCurrency,
  disabled = false
}) {
  const [postnetIndexByMethod, setPostnetIndexByMethod] = useState({})

  const styles = useMemo(() => {
    const next = {}
    if (theme?.accent) next['--pm-accent'] = theme.accent
    if (theme?.accentSoft) next['--pm-accent-soft'] = theme.accentSoft
    if (theme?.text) next['--pm-text'] = theme.text
    if (theme?.muted) next['--pm-muted'] = theme.muted
    if (theme?.border) next['--pm-border'] = theme.border
    if (theme?.surface) next['--pm-surface'] = theme.surface
    if (theme?.surfaceMuted) next['--pm-surface-muted'] = theme.surfaceMuted
    return next
  }, [theme])

  const list = Array.isArray(methods) ? methods : []

  const formatAdjustmentBadge = (method) => {
    const adj = method?.priceAdjustment
    if (!adj) return null
    const rawValue = Number(adj.value)
    if (!Number.isFinite(rawValue) || rawValue === 0) return null
    const isDiscount = adj.sign !== 'surcharge'
    const signLabel = isDiscount ? 'Descuento' : 'Recargo'
    const symbol = isDiscount ? '-' : '+'
    const currency = typeof formatCurrency === 'function'
      ? formatCurrency(Math.abs(rawValue))
      : String(Math.abs(rawValue))
    const valueLabel = adj.kind === 'fixed' ? `${symbol}${currency}` : `${symbol}${Math.abs(rawValue)}%`
    return { signLabel, valueLabel, isDiscount }
  }

  const paymentIcon = (type) => {
    if (type === 'cash') return 'payments'
    if (type === 'bank_transfer') return 'account_balance'
    if (type === 'postnet') return 'credit_card'
    if (type === 'gateway_mercadopago') return 'shoppingmode'
    return 'credit_card'
  }

  const notify = (type, message) => {
    if (typeof onNotify === 'function') onNotify({ type, message })
  }

  const copyToClipboard = async (value) => {
    const text = String(value || '').trim()
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      notify('success', 'Copiado al portapapeles')
    } catch (e) {
      console.error('Error copiando al portapapeles:', e)
      notify('error', 'No se pudo copiar')
    }
  }

  const renderBankTransfer = (method) => {
    const accounts = Array.isArray(method?.config?.accounts) ? method.config.accounts : []
    if (accounts.length === 0) return <div className="pm-subtitle">No hay datos bancarios cargados.</div>
    return (
      <div className="pm-muted-card">
        {accounts.map((acc, idx) => (
          <div key={`${method.id}:acc:${idx}`}>
            <div className="pm-title">{acc.bankName || 'Banco'}</div>
            <div className="pm-field-grid">
              {acc.holder ? (
                <div className="pm-field">
                  <div className="pm-field-label">Titular</div>
                  <div className="pm-field-value">{acc.holder}</div>
                </div>
              ) : null}
              {acc.cbu ? (
                <div className="pm-field">
                  <div className="pm-field-label">
                    CBU
                    <button type="button" className="pm-copy" onClick={() => copyToClipboard(acc.cbu)}>Copiar</button>
                  </div>
                  <div className="pm-field-value">{acc.cbu}</div>
                </div>
              ) : null}
              {acc.alias ? (
                <div className="pm-field">
                  <div className="pm-field-label">
                    Alias
                    <button type="button" className="pm-copy" onClick={() => copyToClipboard(acc.alias)}>Copiar</button>
                  </div>
                  <div className="pm-field-value">{acc.alias}</div>
                </div>
              ) : null}
              {acc.cuit ? (
                <div className="pm-field">
                  <div className="pm-field-label">
                    CUIT
                    <button type="button" className="pm-copy" onClick={() => copyToClipboard(acc.cuit)}>Copiar</button>
                  </div>
                  <div className="pm-field-value">{acc.cuit}</div>
                </div>
              ) : null}
            </div>
            {acc.note ? <div className="pm-subtitle">{acc.note}</div> : null}
          </div>
        ))}
      </div>
    )
  }

  const renderPostnet = (method) => {
    const programs = Array.isArray(method?.config?.programs) ? method.config.programs : []
    if (programs.length === 0) return <div className="pm-subtitle">No hay información de tarjetas cargada.</div>

    const rawIndex = postnetIndexByMethod[method.id]
    const selectedIndex = Number.isFinite(rawIndex) ? rawIndex : 0
    const safeIndex = Math.min(Math.max(0, selectedIndex), Math.max(0, programs.length - 1))
    const selectedProgram = programs[safeIndex]

    return (
      <div>
        <div className="pm-postnet-strip">
          {programs.map((p, idx) => (
            <button
              key={`${method.id}:p:${idx}`}
              type="button"
              className="pm-postnet-icon"
              data-active={idx === safeIndex}
              onClick={() => setPostnetIndexByMethod(prev => ({ ...prev, [method.id]: idx }))}
              aria-label={p.brand || `Tarjeta ${idx + 1}`}
              title={p.brand || `Tarjeta ${idx + 1}`}
            >
              {p.logoUrl ? (
                <img className="pm-postnet-logo" alt={p.brand || 'Tarjeta'} src={p.logoUrl} />
              ) : (
                <div className="pm-postnet-logo" />
              )}
            </button>
          ))}
        </div>

        {selectedProgram ? (
          <div className="pm-muted-card">
            <div className="pm-header">
              <div>
                <div className="pm-title">{selectedProgram.brand || 'Tarjeta'}</div>
                <div className="pm-subtitle">
                  Financiación en cuotas{Array.isArray(selectedProgram.installments) && selectedProgram.installments.length ? ` · ${selectedProgram.installments.length} opciones` : ''}
                </div>
              </div>
            </div>

            {Array.isArray(selectedProgram.installments) && selectedProgram.installments.length > 0 ? (
              <div className="pm-installments">
                {selectedProgram.installments.map((it, itIdx) => (
                  <div key={`${method.id}:p:${safeIndex}:i:${itIdx}`} className="pm-installment">
                    <div className="pm-installment-head">
                      <div className="pm-installment-title">{it.count} cuotas</div>
                      <span className={`pm-chip ${Number(it.interestPercent) ? 'pm-chip-interest' : 'pm-chip-zero'}`}>
                        {Number(it.interestPercent) ? `${it.interestPercent}%` : 'sin interés'}
                      </span>
                    </div>
                    {it.note ? <div className="pm-installment-note">{it.note}</div> : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="pm-subtitle">No hay detalle de cuotas para esta tarjeta.</div>
            )}
          </div>
        ) : null}
      </div>
    )
  }

  const renderDetails = (method) => {
    if (!method) return null
    if (method.type === 'cash') return <div className="pm-subtitle">Coordinamos el pago en efectivo al momento de la entrega o retiro.</div>
    if (method.type === 'bank_transfer') return renderBankTransfer(method)
    if (method.type === 'postnet') return renderPostnet(method)
    if (method.type === 'gateway_mercadopago') return <div className="pm-muted-card pm-subtitle">Al confirmar el pedido se generará el link de pago.</div>
    return null
  }

  return (
    <div className="payment-method-selector" style={styles}>
      {list.map((m) => {
        const active = selectedMethodId === m.id
        const badge = formatAdjustmentBadge(m)
        const subtitle = m.customerNote
          || (m.type === 'gateway_mercadopago'
            ? 'Pagá con tarjeta o saldo a través de MercadoPago'
            : m.type === 'bank_transfer'
              ? 'Transferencia bancaria'
              : m.type === 'postnet'
                ? 'Tarjetas y cuotas disponibles'
                : m.type === 'cash'
                  ? 'Efectivo'
                  : 'Método de pago')
        return (
          <div key={m.id} className="pm-card" data-active={active ? 'true' : 'false'}>
            <button
              type="button"
              className="pm-card-button"
              onClick={() => {
                if (disabled) return
                onSelectMethodId(m.id)
              }}
              disabled={disabled}
            >
              <div className="pm-radio" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="pm-header">
                  <div style={{ display: 'flex', gap: 10, minWidth: 0, alignItems: 'flex-start' }}>
                    <span className="material-symbols-outlined" style={{ color: 'var(--pm-accent)', fontSize: 22, lineHeight: 1 }}>
                      {paymentIcon(m.type)}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div className="pm-title">{m.label}</div>
                      <div className="pm-subtitle">{subtitle}</div>
                    </div>
                  </div>
                  {badge ? (
                    <span className={`pm-badge ${badge.isDiscount ? 'pm-badge-discount' : 'pm-badge-surcharge'}`}>
                      {badge.signLabel} {badge.valueLabel}
                    </span>
                  ) : null}
                </div>
              </div>
            </button>

            {active ? (
              <>
                <div className="pm-divider" />
                <div className="pm-details">
                  {renderDetails(m)}
                </div>
              </>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

export default PaymentMethodSelector
